import {
  attribute,
  cos,
  float,
  hash,
  instanceIndex,
  mix,
  modelViewMatrix,
  oneMinus,
  pow,
  saturate,
  sin,
  smoothstep,
  uniform,
  uv,
  varying,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';
import { Color, type Node, Vector3 } from 'three/webgpu';

const TAU = Math.PI * 2;

/** The uniforms one form occupies while it is on screen. */
class SlotUniforms {
  public readonly base = uniform(new Color(0x88_88_88));
  public readonly shade = uniform(new Color(0xaa_aa_aa));
  public readonly accent = uniform(new Color(0xff_ff_ff));
  /** Idle oscillation speed for this form, radians per second. */
  public readonly rate = uniform(1);
  /** Accumulated slow turn, radians. */
  public readonly spin = uniform(0);
  /** The form's own centre, which it turns about. */
  public readonly centre = uniform(new Vector3());
}

/**
 * Every dial the morph field exposes to the rest of the app. Anything that
 * changes brightness is driven through a rate limiter on the CPU side, which is
 * why nothing here can strobe however hard the music hits.
 */
export class MorphUniforms {
  public readonly slotA = new SlotUniforms();
  public readonly slotB = new SlotUniforms();
  public readonly time = uniform(0);
  /** Blend position, 0 → 1, always in that direction. */
  public readonly blend = uniform(0);
  /** 0 while slot A is the origin, 1 once the roles have swapped. */
  public readonly flip = uniform(0);
  /** Stagger weights, matching `Transition`. */
  public readonly byRandom = uniform(1);
  public readonly byHeight = uniform(0);
  public readonly byRadius = uniform(0);
  public readonly byAngle = uniform(0);
  public readonly byLateral = uniform(0);
  public readonly spread = uniform(0.7);
  public readonly turbulence = uniform(1);
  /** Normalisation frame for the stagger axes: the destination form's extent. */
  public readonly focus = uniform(new Vector3());
  public readonly reach = uniform(8);
  /** Always-on flow, so a held form still breathes. */
  public readonly flow = uniform(0.12);
  /** Musical swell: a slow radial expansion, never a flash. */
  public readonly breath = uniform(0);
  /**
   * The drift oscillators' own clock, advanced faster when the music is busy —
   * this is what makes tentacles and foliage dance to the track, not the wall
   * clock.
   */
  public readonly driftTime = uniform(0);
  /** Beat envelope: spikes on each onset and decays, scaled by the pulse dial. */
  public readonly beatPulse = uniform(0);
  public readonly shimmer = uniform(0.1);
  public readonly glow = uniform(1);
  public readonly size = uniform(0.05);
  public readonly opacity = uniform(0.16);
  public readonly haze = uniform(new Color(0x05_07_12));
  public readonly fogNear = uniform(18);
  public readonly fogFar = uniform(95);
}

/** Everything the material needs, built once from the uniform set. */
export interface MorphGraph {
  readonly position: Node<'vec3'>;
  readonly color: Node<'vec3'>;
  readonly alpha: Node<'float'>;
  readonly scale: Node<'vec2'>;
}

/** Turn a point about the Y axis of `centre`. */
function turn(point: Node<'vec3'>, centre: Node<'vec3'>, angle: Node<'float'>): Node<'vec3'> {
  const rel = point.sub(centre);
  const c = cos(angle);
  const s = sin(angle);
  return vec3(rel.x.mul(c).sub(rel.z.mul(s)), rel.y, rel.x.mul(s).add(rel.z.mul(c))).add(centre);
}

/**
 * A cheap divergence-free-ish flow field from three sums of sines. Real curl
 * noise costs several octaves of gradient noise per particle; at a quarter of a
 * million particles this reads the same and costs almost nothing.
 */
function flowAt(point: Node<'vec3'>, t: Node<'float'>): Node<'vec3'> {
  const p = point.mul(0.13);
  const xa = sin(p.y.mul(1.1).add(t));
  const xb = sin(p.z.mul(0.7).sub(t.mul(0.8)));
  const ya = sin(p.z.mul(0.9).add(t.mul(1.2)));
  const yb = sin(p.x.mul(0.6).sub(t.mul(0.5)));
  const za = sin(p.x.mul(1.3).add(t.mul(0.7)));
  const zb = sin(p.y.mul(0.5).sub(t.mul(0.9)));
  return vec3(xa.add(xb), ya.add(yb), za.add(zb)).mul(0.5);
}

/** Resolve a particle's palette coordinate into a colour: base → shade → accent. */
function shade(tint: Node<'float'>, slot: SlotUniforms): Node<'vec3'> {
  const lower = mix(slot.base, slot.shade, saturate(tint.mul(2.2)));
  const upper = mix(slot.shade, slot.accent, saturate(tint.mul(2.2).sub(1.1)));
  return mix(lower, upper, smoothstep(0.42, 0.58, tint));
}

/** One form's live position: its baked point, breathing on its own phase, turned. */
function slotPosition(
  form: Node<'vec4'>,
  motion: Node<'vec4'>,
  slot: SlotUniforms,
  time: Node<'float'>,
  beat: Node<'float'>,
): Node<'vec3'> {
  const wave = sin(time.mul(slot.rate).add(motion.w));
  // Respiration wave modulates the drift amplitude smoothly.
  const amp = beat.mul(0.65).add(1);
  return turn(form.xyz.add(motion.xyz.mul(wave).mul(amp)), slot.centre, slot.spin);
}

/**
 * Per-particle morph delay. Giving each particle its own start time is the whole
 * trick: the form does not crossfade, it comes apart and reassembles, and the
 * axis the delay follows decides whether that looks like a tide, a ripple, a
 * spiral or a curtain.
 */
function staggeredBlend(destination: Node<'vec3'>, u: MorphUniforms): Node<'float'> {
  const rel = destination.sub(u.focus);
  const seeded = hash(float(instanceIndex).add(11.3));
  const height = rel.y.div(u.reach).mul(0.5).add(0.5);
  const radius = saturate(rel.length().div(u.reach));
  const angle = rel.z
    .atan(rel.x)
    .mul(1 / TAU)
    .add(0.5);
  const lateral = rel.x.div(u.reach).mul(0.5).add(0.5);
  const offset = seeded
    .sub(0.5)
    .mul(u.byRandom)
    .add(height.sub(0.5).mul(u.byHeight))
    .add(radius.sub(0.5).mul(u.byRadius))
    .add(angle.sub(0.5).mul(u.byAngle))
    .add(lateral.sub(0.5).mul(u.byLateral));
  const delay = saturate(offset.add(0.5)).mul(u.spread);
  return smoothstep(delay, delay.add(oneMinus(u.spread)), u.blend);
}

/** Build the node graph for the morph field. */
export function buildMorphGraph(u: MorphUniforms): MorphGraph {
  const formA = attribute<'vec4'>('form0', 'vec4');
  const formB = attribute<'vec4'>('form1', 'vec4');
  const motionA = attribute<'vec4'>('motion0', 'vec4');
  const motionB = attribute<'vec4'>('motion1', 'vec4');

  const pointA = slotPosition(formA, motionA, u.slotA, u.driftTime, u.beatPulse);
  const pointB = slotPosition(formB, motionB, u.slotB, u.driftTime, u.beatPulse);

  // `flip` is exactly 0 or 1, so these mixes are exact selections.
  const destination = mix(pointB, pointA, u.flip);
  const eased = staggeredBlend(destination, u);
  const towards = mix(eased, oneMinus(eased), u.flip);

  // Turbulence peaks at the midpoint, where the form is no longer either thing.
  const bulge = sin(eased.mul(Math.PI));
  const merged = mix(pointA, pointB, towards);
  const churn = u.flow.add(bulge.mul(u.turbulence));
  const stream = flowAt(merged, u.time.mul(0.22));
  const flowed = merged.add(stream.mul(churn));
  // The slow bass swell plus an organic radial respiration wave.
  const swell = u.breath.mul(0.05).add(u.beatPulse.mul(0.038)).add(1);
  const breathing = u.focus.add(flowed.sub(u.focus).mul(swell));

  const tint = mix(formA.w, formB.w, towards);
  const litA = shade(formA.w, u.slotA);
  const litB = shade(formB.w, u.slotB);
  const accent = smoothstep(0.68, 0.95, tint);
  const seeded = hash(float(instanceIndex).add(3.7));

  // Electric cyan and sapphire plasma spectral shift
  const spectralPhase = tint
    .mul(TAU * 2.5)
    .add(u.time.mul(0.45))
    .add(breathing.y.mul(0.14))
    .add(seeded.mul(TAU));
  const electricPlasma = vec3(
    sin(spectralPhase).mul(0.25).add(0.05),
    sin(spectralPhase.add(1.3)).mul(0.45).add(0.55),
    sin(spectralPhase.add(2.6)).mul(0.4).add(0.6),
  );

  // Fast electric plasma twinkle
  const twinklePhase = u.time.mul(4.2).add(seeded.mul(TAU * 11));
  const twinkle = sin(twinklePhase).mul(0.5).add(0.5);

  const viewDepth = modelViewMatrix.mul(vec4(breathing, 1)).z.negate();
  const distance = varying(saturate(smoothstep(u.fogNear, u.fogFar, viewDepth)), 'morphDistance');

  // A restrained, organic luminescence breath on beats.
  const beatGlow = u.glow.mul(u.beatPulse.mul(0.08).add(0.92));
  const baseColor = mix(litA, litB, towards);
  // Infuse electric cyan/blue plasma into the highlights and shimmer
  const plasmaColor = mix(baseColor, electricPlasma, accent.mul(0.4).add(u.shimmer.mul(0.35)));
  const body = plasmaColor.mul(accent.mul(twinkle).mul(u.shimmer.mul(0.75)).add(1)).mul(beatGlow);
  const color = varying(mix(body, u.haze, distance.mul(0.75)), 'morphColor');

  const fromCentre = uv().sub(0.5).length().mul(2);
  const disc = pow(saturate(oneMinus(fromCentre)), 1.18);
  const alpha = disc
    .mul(u.opacity)
    .mul(seeded.mul(0.35).add(0.75))
    .mul(oneMinus(distance.mul(0.65)));

  const spread = u.size.mul(seeded.mul(0.6).add(0.85)).mul(bulge.mul(0.5).add(1));
  const scale = vec2(spread.mul(accent.mul(0.4).add(1)));

  return { position: breathing, color, alpha, scale };
}
